import { EditIcon } from "@assets/images/editIcon";
import { UploadIcon } from "@assets/images/uploadIcon";
import {
  Avatar,
  Flex,
  FormLabel,
  IconButton,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { CustomText } from "@root/components/common/components";
import { getLoggedInUser, selectProfile } from "@root/store/selectors/entities";
import { getUserProfile } from "@root/store/thunks";
import { uploadFile, userProfile } from "@root/store/thunks/userProfile";
import { Formik } from "formik";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  StyledAvatarContainer,
  StyledAvatarTitle,
  StyledButton,
  StyledDeleteButton,
  StyledFormControl,
  StyledInput,
  StyledProfileContainer,
  StyledSectionDescription,
  StyledSubmitButton,
  StyledUserInfoBox,
  StyledUserInfoHeader,
} from "../userProfile/style";

export const UserProfile = () => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const currentUser: any = useSelector(getLoggedInUser);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [fileURL, setFileURL] = useState<any>("");
  const profile = useSelector(selectProfile);
  const API_KEY = profile?.API_Key;
  const inputRef: any = useRef(null);

  const toggleEditing = () => {
    setIsEditing(!isEditing);
  };

  const initialValues = {
    email: currentUser?.email || "",
    fullName: currentUser?.fullname || "",
    bio: currentUser?.bio || "",
  };

  const handleApiCall = (payload: allAnyTypes) => {
    dispatch(
      userProfile({
        payload,
        onSuccess: () => {
          setIsEditing(false);
          setLoading(false);
        },
        onError: (error: any) => {
          setLoading(false);
          console.error("Failed to update profile:", error);
        },
      })
    );
    dispatch(getUserProfile({}));
  };

  const handleSubmit = (values: any) => {
    setLoading(true);
    const { fullName, bio } = values;
    const payload: any = { fullname: fullName, bio };
    if (fileURL) {
      payload.avatar = fileURL;
    }
    inputRef.current.value = null;
    handleApiCall(payload);
    setFileURL("");
  };

  const fileUploadClick = () => {
    if (inputRef?.current) {
      inputRef?.current?.click();
    }
  };

  const handleDelete = () => {
    if (selectedFile) {
      setSelectedFile(null);
      setFileURL("");
      inputRef.current.value = null;
      return;
    }
    const payload: any = { avatar: "" };
    handleApiCall(payload);
  };

  const onFileChange = (file: any) => {
    setSelectedFile(file);
    const data = new FormData();
    data.append("file", file);
    console.log("come in file change");
    dispatch(
      uploadFile({
        payload: data,
        onSuccess: (res: any) => {
          setFileURL(res);
        },
      })
    );
  };

  return (
    <Flex direction="column" w="full">
      <StyledProfileContainer>
        <CustomText
          fontSize={"20px"}
          fontWeight={500}
          mb={4}
          text={t("profile.myProfile")}
        />
        <StyledSectionDescription>
          {t("profile.manageProfileInfo")}
        </StyledSectionDescription>

        <StyledUserInfoBox>
          <StyledUserInfoHeader>
            <Text fontSize="md" fontWeight="bold">
              {t("profile.userInfo")}
            </Text>
            <Flex
              ml={"auto"}
              justifyContent={"center"}
              alignItems={"center"}
              onClick={toggleEditing}
            >
              <IconButton
                aria-label={t("common.edit")}
                icon={<EditIcon color={"theme.button.primary"} />}
                size="sm"
                bg="transparent"
                ml="auto"
              />
              <CustomText
                color={"theme.button.primary"}
                cursor={"pointer"}
                fontWeight={700}
                text={t("translate.edit")}
              />
            </Flex>
          </StyledUserInfoHeader>
          <StyledAvatarTitle>{t("profile.avatar")}</StyledAvatarTitle>
          <StyledAvatarContainer>
            <Avatar
              name={currentUser?.fullname}
              size="lg"
              bgColor="theme.alert.200"
              color={"theme.button.primary"}
              src={
                selectedFile
                  ? URL?.createObjectURL(selectedFile)
                  : currentUser?.avatar
                  ? currentUser?.avatar
                  : ""
              }
              mb={4}
            />
            <Flex justify="center" flexWrap={"wrap"} gap={"16px"} mt={2}>
              <StyledButton
                size="lg"
                mr={2}
                leftIcon={<UploadIcon />}
                onClick={fileUploadClick}
                isDisabled={!isEditing}
              >
                {t("profile.uploadNewPicture")}
              </StyledButton>
              {((isEditing && selectedFile) || profile?.avatar) && (
                <StyledDeleteButton
                  size="lg"
                  isDisabled={!isEditing && currentUser?.user?.avatar}
                  onClick={handleDelete}
                >
                  {t("profile.delete")}
                </StyledDeleteButton>
              )}
            </Flex>
          </StyledAvatarContainer>
          <Formik initialValues={initialValues} onSubmit={handleSubmit}>
            {({ handleChange, handleSubmit, values }) => (
              <form onSubmit={handleSubmit}>
                <StyledFormControl>
                  <FormLabel>{t("translate.APIkey")}</FormLabel>
                  <StyledInput
                    bgColor={"theme.gray.200"}
                    isDisabled
                    value={API_KEY}
                  />
                </StyledFormControl>
                <StyledFormControl>
                  <FormLabel>{t("profile.emailAddress")}</FormLabel>
                  <StyledInput
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    disabled={true}
                    bgColor={"theme.gray.200"}
                  />
                </StyledFormControl>
                <StyledFormControl>
                  <FormLabel>{t("profile.fullName")}</FormLabel>
                  <StyledInput
                    type="text"
                    name="fullName"
                    value={values.fullName}
                    onChange={handleChange}
                    disabled={!isEditing}
                  />
                </StyledFormControl>
                <StyledFormControl>
                  <FormLabel>{t("profile.introduction")}</FormLabel>
                  <Textarea
                    name="bio"
                    value={values.bio}
                    onChange={handleChange}
                    borderColor="theme.gray.100"
                    outline={0}
                    shadow={0}
                    _focus={{ borderColor: "theme.gray.200", outline: 0 }}
                    _dark={{ borderColor: "theme.gray.200" }}
                    disabled={!isEditing}
                  />
                </StyledFormControl>
                {isEditing && (
                  <StyledSubmitButton isLoading={loading} type="submit">
                    {t("profile.save")}
                  </StyledSubmitButton>
                )}
              </form>
            )}
          </Formik>
        </StyledUserInfoBox>
      </StyledProfileContainer>
      <input
        type="file"
        onChange={(e: any) => onFileChange(e?.target?.files?.[0])}
        style={{ display: "none" }}
        ref={inputRef}
      />
    </Flex>
  );
};

export default UserProfile;
